/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Route table for the SPA. Public home/callback vs Auth0-guarded dashboard shell.
 * {@code /dashboard/...} URLs rewrite to the current top-level paths (/search, /milemaker, …).
 */

import React from "react";
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import {PageLoader} from "./components/PageLoader";
import {AuthenticationGuard} from "./components/AuthenticationGuard";
import {CallbackPage} from "./pages/CallbackPage";
import {HomePage} from "./pages/HomePage";
import {NotFoundPage} from "./pages/NotFoundPage";
import Dashboard from "./pages/Dashboard";
import {NoSubscription} from "./pages/NoSubscription";
import {useUserData} from "./hooks/useUserData";
import {useAuth0} from "@auth0/auth0-react";
import {DASHBOARD_SHELL_PATHS, ROUTES, rewriteLegacyDashboardPath} from "./config/routes";

function LegacyDashboardRedirect() {
    const { pathname, search } = useLocation();
    // Old bookmarks were /dashboard/search; tabs now live at /search, /setup, etc.
    return <Navigate to={`${rewriteLegacyDashboardPath(pathname)}${search}`} replace />;
}

export const App = () => {
    const {isLoading: auth0Loading} = useAuth0();
    const {isLoading: userDataLoading} = useUserData();

    // Show loader until Auth0 is fully initialized
    // This prevents flash of header/nav before Auth0 is ready
    if (auth0Loading || userDataLoading) {
        return (
            <div className="page-layout">
                <PageLoader/>
            </div>
        );
    }

    return (
        <Routes>
            <Route path={ROUTES.HOME} element={<HomePage/>}/>
            {DASHBOARD_SHELL_PATHS.map((path) => (
                <Route key={path} path={path} element={<AuthenticationGuard component={Dashboard}/>}/>
            ))}
            <Route path={`${ROUTES.DASHBOARD}/*`} element={<AuthenticationGuard component={LegacyDashboardRedirect}/>}/>
            <Route path={ROUTES.CALLBACK} element={<CallbackPage/>}/>
            <Route path={ROUTES.NO_SUBSCRIPTION} element={<AuthenticationGuard component={NoSubscription}/>}/>
            <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage/>}/>
        </Routes>
    );
};
